import { useEffect, useRef } from 'react';
import { useSandpack } from '@codesandbox/sandpack-react';
import type { SandboxErrorHandler } from './SandboxErrorBoundary';

/**
 * Watches Sandpack's internal error state for user JSX runtime/compilation
 * errors and reports them via the onError callback. These errors happen inside
 * the cross-origin iframe — they don't crash the host app, but without this
 * reporter they'd be silently swallowed (only visible in the Sandpack error
 * overlay inside the iframe).
 *
 * Must be rendered inside a SandboxProvider / SandpackProvider.
 */
export function SandboxErrorReporter({ onError }: { onError?: SandboxErrorHandler }): null {
  const { sandpack } = useSandpack();
  const lastReported = useRef<string | null>(null);

  useEffect(() => {
    const errorMessage = sandpack.error?.message;
    if (!errorMessage || !onError) {
      lastReported.current = null;
      return;
    }

    // Deduplicate — don't report the same error repeatedly as the user types
    if (errorMessage === lastReported.current) return;
    lastReported.current = errorMessage;

    onError(
      new Error(errorMessage),
      { source: 'runtime', message: errorMessage },
    );
  }, [sandpack.error, onError]);

  return null;
}
